/**
 * 123.95 => 123,95€ (fr-FR)
 * 123.95 => €123.95 (en-US)
 */

import { getSiteLocalization } from '@/lib/i18n/config/site-locales'
import { formatNumber } from './number.formatter'

/**
 * Format a number as a price, with or without the currency symbol.
 *
 * @param amount – the numeric amount
 * @param withCurrency – whether to include the currency symbol (default: true)
 * @param locale – e.g. 'fr-FR' or 'en-US' (default: site locale)
 *
 * @returns formatted string, e.g.
 *   formatPrice(123.95)                // "123,95€"
 *   formatPrice(123.95, true)          // "123,95€"
 *   formatPrice(123.95, false)         // "123,95"
 *   formatPrice(123.95, true, 'en-US') // "$123.95"
 *   formatPrice(123.95, false, 'en-US')// "123.95"
 */
export function formatPrice(
  amount: number,
  withCurrency = true,
  locale = getSiteLocalization().locale,
): string {
  if (!withCurrency) {
    // Plain decimal format, no currency symbol
    return formatNumber(amount, locale)
  }

  // Full currency formatting
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)

  // Strip the non-breaking space that Intl inserts before/after the symbol
  return formatted.replace(/\u00A0(?=[^\d]*$)/, '')
}
